/**
 * Import.jsx
 * A spreadsheet of posts, in — and everything Kreeboom knows, out.
 *
 * The screen is built around one refusal: it will not create anything until
 * you have seen a row-by-row account of what it would create. Choosing a file
 * only ever previews. The button that writes says how many posts it is about
 * to make, and whether they will be scheduled or left as drafts.
 *
 * Drafts is the default, and deliberately so. "Import 40 posts" and "put 40
 * posts live over the next fortnight" are very different acts, and the second
 * one should take a second click.
 */

import { useEffect, useRef, useState } from 'react';
import { useBrands } from '../hooks/useBrands.jsx';
import { api } from '../services/api.js';
import NoBrands from '../components/NoBrands.jsx';
import { Warning, Check, Upload, Download, Layers } from '../components/Icons.jsx';
import { formatLocalInput } from '../utils/time.js';

export default function Import() {
  const { brands, loading, error: brandsError } = useBrands();
  const [brandId, setBrandId] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [schedule, setSchedule] = useState(false);
  const [defaultTime, setDefaultTime] = useState('09:00');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [dropping, setDropping] = useState(0);
  // What the Media column can actually refer to. Loaded per brand, because the
  // alternative is typing a filename from memory and finding out it was wrong
  // one row at a time.
  const [library, setLibrary] = useState(null);
  const input = useRef(null);

  const brand = brands.find((b) => b.id === brandId);

  useEffect(() => {
    if (!brandId) {
      setLibrary(null);
      return;
    }
    let stopped = false;
    api.bulk
      .media(brandId)
      .then((d) => !stopped && setLibrary(d))
      // A missing list must never stop a file being imported.
      .catch(() => !stopped && setLibrary(null));
    return () => {
      stopped = true;
    };
  }, [brandId]);

  if (!loading && brands.length === 0) return <NoBrands error={brandsError} />;

  async function choose(chosen) {
    if (!chosen) return;
    if (!brandId) {
      setError(new Error('Pick a brand first — an import goes into one brand.'));
      return;
    }

    setFile(chosen);
    setResult(null);
    setError(null);
    setBusy(true);
    try {
      setPreview(await api.bulk.preview({ brandId, file: chosen, defaultTime }));
    } catch (e) {
      setError(e);
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.bulk.import({ brandId, file, defaultTime, schedule });
      setResult(res);
      setPreview(null);
      setFile(null);
      if (input.current) input.current.value = '';
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setPreview(null);
    setResult(null);
    setFile(null);
    setError(null);
    if (input.current) input.current.value = '';
  }

  const dragProps = {
    onDragEnter: (e) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      setDropping((d) => d + 1);
    },
    onDragOver: (e) => {
      if (e.dataTransfer?.types?.includes('Files')) e.preventDefault();
    },
    onDragLeave: () => setDropping((d) => Math.max(0, d - 1)),
    onDrop: (e) => {
      e.preventDefault();
      setDropping(0);
      choose(e.dataTransfer?.files?.[0]);
    },
  };

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Import &amp; export</div>
          <h1>Posts from a spreadsheet</h1>
          <p>
            A month of posts in one file. Nothing is created until you have seen exactly what it
            would create — and nothing an import creates ever publishes on its own.
          </p>
        </div>
        <div className="page-head__actions">
          <a className="btn" href="/api/bulk/template" download>
            <Download /> Template
          </a>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {/* ---------- what came of the last import ---------- */}
      {result && <ImportResult result={result} onDone={reset} />}

      {/* ---------- choose a file ---------- */}
      {!preview && !result && (
        <section className={`card dropzone${dropping > 0 ? ' is-dropping' : ''}`} {...dragProps}>
          <div className="card__head">
            <span className="tile">
              <Upload size={21} />
            </span>
            <div className="card__head-text">
              <h2>Choose a file</h2>
              <p>A CSV exported from Excel, Numbers or Google Sheets.</p>
            </div>
          </div>

          <div className="field">
            <label htmlFor="import-brand">Brand</label>
            <select
              id="import-brand"
              value={brandId}
              onChange={(e) => {
                setBrandId(e.target.value);
                setError(null);
              }}
            >
              <option value="">Choose a brand…</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            <p className="hint">
              Every row goes into this brand, and images are matched against its library.
            </p>
          </div>

          <div className="field">
            <label htmlFor="import-time">Time to use when a row gives a date but no time</label>
            <input
              id="import-time"
              type="time"
              value={defaultTime}
              onChange={(e) => setDefaultTime(e.target.value)}
              style={{ maxWidth: 160 }}
            />
          </div>

          <div className="field">
            <label htmlFor="import-file">Spreadsheet</label>
            <input
              id="import-file"
              ref={input}
              type="file"
              accept=".csv,text/csv"
              disabled={!brandId || busy}
              onChange={(e) => choose(e.target.files?.[0])}
            />
            <p className="hint">
              {busy ? 'Reading it…' : 'Or drag one onto this panel. Nothing is created yet.'}
            </p>
          </div>

          {brandId && <MediaNames library={library} brandName={brand?.name} />}

          <details className="details">
            <summary>Which columns are understood</summary>
            <ul className="plainlist">
              <li>
                <b>When</b> — <code>2026-10-01 17:00</code>, <code>01/10/2026</code> or{' '}
                <code>1 Oct 2026</code>. Read day-first, London time. Leave blank for an
                unscheduled draft.
              </li>
              <li>
                <b>Platforms</b> — <code>facebook, instagram</code>, or <code>FB + IG</code>.
                Blank means every connected platform for the brand.
              </li>
              <li>
                <b>Format</b> — post, carousel, story or link.
              </li>
              <li>
                <b>Caption</b> for both, or <b>Facebook caption</b> and <b>Instagram caption</b>{' '}
                separately. The per-platform one wins.
              </li>
              <li>
                <b>Media</b> — the spreadsheet holds <em>filenames, not pictures</em>. Upload the
                images to the Library first, then write their names here, separated by{' '}
                <code>|</code>. Matching ignores capitals, and the extension is optional.
              </li>
              <li>
                <b>Hashtags</b> — added to the Facebook caption, and to the Instagram first
                comment, which is where each platform actually shows them.
              </li>
              <li>
                <b>Link</b>, <b>First comment</b>, <b>Goal</b>, <b>Notes</b> — optional.
              </li>
            </ul>
          </details>
        </section>
      )}

      {/* ---------- the preview ---------- */}
      {preview && (
        <Preview
          preview={preview}
          brand={brand}
          busy={busy}
          schedule={schedule}
          setSchedule={setSchedule}
          onCancel={reset}
          onCommit={commit}
          showAll={showAll}
          setShowAll={setShowAll}
        />
      )}

      {/* ---------- export ---------- */}
      <section className="card">
        <div className="card__head">
          <span className="tile">
            <Layers size={21} />
          </span>
          <div className="card__head-text">
            <h2>Export everything</h2>
            <p>
              Every post, its captions, when it went out and how it did — as a spreadsheet. This is
              what makes the data yours rather than something you can only see through Kreeboom.
            </p>
          </div>
        </div>

        <div className="btn-row">
          <a className="btn" href={exportHref(brandId)} download>
            <Download /> {brand ? `Export ${brand.name}` : 'Export every brand'}
          </a>
          <a className="btn" href={exportHref(brandId, 'published')} download>
            <Download /> Published only
          </a>
        </div>
      </section>
    </>
  );
}

function exportHref(brandId, status) {
  const q = new URLSearchParams();
  if (brandId) q.set('brand_id', brandId);
  if (status) q.set('status', status);
  const query = q.toString();
  return `/api/bulk/export${query ? `?${query}` : ''}`;
}

/** Row by row, before anything is written. */
function Preview({ preview, brand, busy, schedule, setSchedule, onCancel, onCommit, showAll, setShowAll }) {
  if (preview.fatal) {
    return (
      <section className="card">
        <div className="notice notice--error">
          <Warning />
          <span>{preview.fatal}</span>
        </div>
        <button className="btn" onClick={onCancel}>
          Choose another file
        </button>
      </section>
    );
  }

  const { summary, rows } = preview;
  const shown = showAll ? rows : rows.slice(0, 20);
  const willSchedule = schedule ? summary.scheduled : 0;

  return (
    <section className="card">
      <div className="card__head">
        <div className="card__head-text">
          <h2>{preview.filename}</h2>
          <p>
            {summary.total} row{summary.total === 1 ? '' : 's'} for {brand?.name}. Nothing has been
            created yet.
          </p>
        </div>
      </div>

      <div className="stat-row">
        <div className="stat">
          <div className="stat__value">{summary.ready}</div>
          <div className="stat__label">Ready to import</div>
        </div>
        <div className="stat">
          <div className="stat__value">{summary.problems}</div>
          <div className="stat__label">Will be skipped</div>
        </div>
        <div className="stat">
          <div className="stat__value">{summary.scheduled}</div>
          <div className="stat__label">Have a future time</div>
        </div>
      </div>

      {summary.ready === 0 && (
        <div className="notice notice--error">
          <Warning />
          <span>No row in this file can be imported yet. Every problem is listed below.</span>
        </div>
      )}

      <div className="importrows">
        {shown.map((row) => (
          <PreviewRow key={row.line} row={row} />
        ))}
      </div>

      {rows.length > shown.length && (
        <button className="btn btn--sm" onClick={() => setShowAll(true)}>
          Show the other {rows.length - shown.length}
        </button>
      )}

      {summary.ready > 0 && (
        <>
          <label className="checkline" style={{ marginTop: 18 }}>
            <input
              type="checkbox"
              checked={schedule}
              onChange={(e) => setSchedule(e.target.checked)}
            />
            <span>
              <b>Schedule them as well as importing them.</b>
              <span className="checkline__hint">
                {summary.scheduled === 0
                  ? 'No row has a future time, so this would do nothing.'
                  : `${summary.scheduled} row${summary.scheduled === 1 ? '' : 's'} would go into the ` +
                    'scheduler and publish without further approval. Leave it off to import them as ' +
                    'drafts with their times filled in.'}
              </span>
            </span>
          </label>

          <div className="btn-row" style={{ marginTop: 14 }}>
            <button className="btn btn--primary" onClick={onCommit} disabled={busy}>
              <Check />
              {busy
                ? 'Importing…'
                : `Import ${summary.ready} post${summary.ready === 1 ? '' : 's'}` +
                  (willSchedule ? ` and schedule ${willSchedule}` : ' as drafts')}
            </button>
            <button className="btn" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
          </div>
        </>
      )}

      {summary.ready === 0 && (
        <button className="btn" onClick={onCancel} style={{ marginTop: 14 }}>
          Choose another file
        </button>
      )}
    </section>
  );
}

function PreviewRow({ row }) {
  const bad = row.problems.length > 0;

  return (
    <div className={`importrow${bad ? ' importrow--bad' : ''}`}>
      <span className="importrow__line">{row.line}</span>

      <div className="importrow__body">
        <div className="importrow__top">
          {row.when ? (
            <span className={`badge${row.schedulable ? '' : ' badge--warn'}`}>
              {formatLocalInput(row.when)}
            </span>
          ) : (
            <span className="badge">No time — draft</span>
          )}
          {row.platforms.map((p) => (
            <span key={p} className="badge badge--accent">
              {p}
            </span>
          ))}
          {row.format !== 'post' && <span className="badge">{row.format}</span>}
          {row.media.map((m) => (
            <span key={m.id} className="badge badge--ok">
              {m.name}
            </span>
          ))}
        </div>

        <p className="importrow__caption">{row.preview}</p>

        {row.problems.map((p, i) => (
          <p key={i} className="importrow__problem">
            <Warning size={13} /> {p}
          </p>
        ))}
        {row.warnings.map((w, i) => (
          <p key={i} className="importrow__warning">
            {w}
          </p>
        ))}
      </div>
    </div>
  );
}

function ImportResult({ result, onDone }) {
  return (
    <section className="card">
      <div className="notice notice--ok">
        <Check />
        <span>
          Imported {result.created.length} post{result.created.length === 1 ? '' : 's'}
          {result.scheduled > 0 && `, ${result.scheduled} scheduled`}
          {result.scheduled === 0 && result.created.length > 0 && ' as drafts'}.
        </span>
      </div>

      {result.notScheduled?.length > 0 && (
        <div className="notice notice--info">
          <Warning />
          <span>
            {result.notScheduled.length} could not be scheduled and were left as drafts with their
            times filled in: {result.notScheduled[0].reason}
          </span>
        </div>
      )}

      {result.skipped.length > 0 && (
        <details className="details" open>
          <summary>{result.skipped.length} row(s) skipped</summary>
          <ul className="plainlist">
            {result.skipped.map((s) => (
              <li key={s.line}>
                <b>Line {s.line}</b> — {s.reasons.join(' ')}
              </li>
            ))}
          </ul>
        </details>
      )}

      {result.failed.length > 0 && (
        <details className="details" open>
          <summary>{result.failed.length} row(s) failed while being created</summary>
          <ul className="plainlist">
            {result.failed.map((f) => (
              <li key={f.line}>
                <b>Line {f.line}</b> — {f.reason}
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="btn-row" style={{ marginTop: 14 }}>
        <a className="btn btn--primary" href="/drafts">
          Open Drafts
        </a>
        <button className="btn" onClick={onDone}>
          Import another file
        </button>
      </div>
    </section>
  );
}

/**
 * The filenames a spreadsheet can actually use.
 *
 * The Media column matches on the name a file had when it was uploaded, and
 * nothing else in the app ever shows you what that was — so without this panel
 * the column is guesswork, and you find out you guessed wrong one row at a
 * time in the preview.
 *
 * The count of images that cannot be matched is shown just as prominently.
 * Anything uploaded before Kreeboom recorded filenames has none, works
 * perfectly well everywhere else, and can never be named in a CSV. Discovering
 * that from a failed import would be much worse than being told here.
 */
function MediaNames({ library, brandName }) {
  if (!library) return null;

  const { usable, unnamed, note } = library;

  return (
    <details className="details">
      <summary>
        Image names you can use ({usable.length} available
        {unnamed > 0 ? `, ${unnamed} cannot be matched` : ''})
      </summary>

      {usable.length === 0 ? (
        <p className="hint" style={{ marginTop: 10 }}>
          Nothing in {brandName}’s library can be referred to by name yet. Upload an image on the
          Library or Create screen and its filename becomes usable here straight away.
        </p>
      ) : (
        <>
          <p className="hint" style={{ marginTop: 10 }}>
            Put any of these in the <b>Media</b> column. Click one to copy it.
          </p>
          <div className="chips" style={{ marginTop: 8 }}>
            {usable.map((m) => (
              <button
                key={m.id}
                type="button"
                className="chip-btn"
                title={`${m.kind}${m.aspect ? `, ${m.aspect}` : ''} — click to copy`}
                onClick={() => navigator.clipboard?.writeText(m.name)}
              >
                {m.name}
              </button>
            ))}
          </div>
        </>
      )}

      {note && (
        <div className="notice notice--info notice--compact" style={{ marginTop: 12 }}>
          <Warning />
          <span>{note}</span>
        </div>
      )}
    </details>
  );
}
